import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { AbonnementAbandonne, MerciAbonnement } from './Abonnement';
import { Accueil } from './Accueil';
import { Connexion } from './Connexion';
import { Coquille } from './Coquille';
import { CreerDossier } from './CreerDossier';
import { estCategorieDossier, type CategorieDossier } from '@repere/core';
import { Dossier } from './Dossier';
import { Inscription } from './Inscription';
import { MonCompte } from './MonCompte';
import { MotDePasseOublie } from './MotDePasseOublie';
import { Options } from './Options';
import { ReinitialiserMotDePasse } from './ReinitialiserMotDePasse';
import { Rejoindre } from './Rejoindre';
import { SupprimerDossier } from './SupprimerDossier';
import { VerifierEmail } from './VerifierEmail';
import type { Session } from '../auth/session';

/**
 * Arborescence de routes.
 *
 * La session est passée au routeur par son contexte : la protection d'une route est donc
 * évaluée avant le rendu, et non par un effet après coup qui laisserait apparaître un
 * écran privé le temps d'un battement.
 *
 * **Deux populations, deux territoires** (arbitrage du 2026-08-31). Un compte `admin` est un
 * compte d'exploitation : il entre dans `/b-admin` et dans `/self.diagnose`, et le produit
 * des parents lui est fermé — ce n'est pas un parent qui aurait des pouvoirs en plus. La
 * séparation est **tenue par l'API** (`JwtAuthGuard`, qui refuse par défaut toute route non
 * annotée `@BackOffice()` à un jeton d'exploitation) ; ce qui suit n'évite que des écrans
 * vides et des allers-retours.
 */

export interface ContexteRouteur {
  session: Session;
}

const exigeSession = ({ context }: { context: unknown }): void => {
  const { session } = context as ContexteRouteur;
  if (session.chargement) return;
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- signal de navigation du routeur
  if (!session.profil) throw redirect({ to: '/connexion' });
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- signal de navigation du routeur
  if (session.profil.role === 'admin') throw redirect({ to: '/b-admin' });
};

/**
 * Garde du back-office (T-058).
 *
 * Elle **n'autorise rien** : elle évite d'afficher un écran que l'API refusera de remplir.
 * L'autorisation est côté serveur, revérifiée en base à chaque requête (`AdminGuard`) —
 * une garde de routeur vit dans le navigateur, et ce qui vit dans le navigateur ne protège
 * personne.
 *
 * Un non-administrateur est renvoyé à l'accueil, pas à une page « accès refusé » : celle-ci
 * confirmerait l'existence du back-office à qui cherche justement à la deviner.
 */
const exigeAdmin = ({ context }: { context: unknown }): void => {
  const { session } = context as ContexteRouteur;
  if (session.chargement) return;
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- signal de navigation du routeur
  if (!session.profil) throw redirect({ to: '/connexion' });
  // eslint-disable-next-line @typescript-eslint/only-throw-error -- signal de navigation du routeur
  if (session.profil.role !== 'admin') throw redirect({ to: '/' });
};

const racine = createRootRoute({
  component: () => (
    <Coquille>
      <Outlet />
    </Coquille>
  ),
});

const connexion = createRoute({
  getParentRoute: () => racine,
  path: '/connexion',
  // Le retour d'un fournisseur d'identité atterrit ici (T-041) : `reprise` porte le code
  // à usage unique, `erreur` le motif quand le parcours n'a pas abouti. Ni l'un ni l'autre
  // n'est un jeton — ceux-ci s'échangent hors de l'URL.
  validateSearch: (search: Record<string, unknown>): { reprise?: string; erreur?: string } => ({
    ...(typeof search.reprise === 'string' && search.reprise ? { reprise: search.reprise } : {}),
    ...(typeof search.erreur === 'string' && search.erreur ? { erreur: search.erreur } : {}),
  }),
  component: function EcranConnexion() {
    const naviguer = useNavigate();
    const { reprise, erreur } = useSearch({ from: '/connexion' });
    return (
      <Connexion
        surConnexion={() => void naviguer({ to: '/' })}
        surInscription={() => void naviguer({ to: '/inscription' })}
        surMotDePasseOublie={() => void naviguer({ to: '/mot-de-passe-oublie' })}
        {...(reprise !== undefined ? { reprise } : {})}
        {...(erreur !== undefined ? { echecFournisseur: erreur } : {})}
      />
    );
  },
  beforeLoad: ({ context }) => {
    const { session } = context as ContexteRouteur;
    if (!session.profil) return;
    // Un compte d'exploitation qui vient de se connecter n'a rien à faire sur l'accueil :
    // `exigeSession` l'y renverrait aussitôt, et le parent verrait un battement d'écran.
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- signal de navigation du routeur
    throw redirect({ to: session.profil.role === 'admin' ? '/b-admin' : '/' });
  },
});

const inscription = createRoute({
  getParentRoute: () => racine,
  path: '/inscription',
  component: function EcranInscription() {
    const naviguer = useNavigate();
    return (
      <Inscription
        surConnexion={() => void naviguer({ to: '/connexion' })}
        surSession={() => void naviguer({ to: '/' })}
      />
    );
  },
});

const accueil = createRoute({
  getParentRoute: () => racine,
  path: '/',
  component: Accueil,
  beforeLoad: exigeSession,
});

const creerDossier = createRoute({
  getParentRoute: () => racine,
  path: '/dossiers/creer',
  beforeLoad: exigeSession,
  component: function EcranCreation() {
    const naviguer = useNavigate();
    return (
      <CreerDossier
        surCreation={(id) => void naviguer({ to: '/dossiers/$dossierId', params: { dossierId: id } })}
      />
    );
  },
});

const dossier = createRoute({
  getParentRoute: () => racine,
  path: '/dossiers/$dossierId',
  beforeLoad: exigeSession,
  // La catégorie ouverte vit dans l'URL, pas seulement en mémoire : c'est ce qui rend
  // partageable un lien vers la messagerie d'un dossier, et ce qui fait revenir le bouton
  // « précédent » là où le parent était. Une valeur inconnue retombe sur « Aujourd'hui »
  // plutôt que sur un écran vide.
  //
  // Le paramètre est **facultatif** : « Aujourd'hui » se lit sur l'URL nue, et les liens
  // qui pointent simplement vers un dossier n'ont pas à porter une catégorie qu'ils ne
  // choisissent pas.
  validateSearch: (
    search: Record<string, unknown>,
  ): { vue?: CategorieDossier; enfant?: string } => ({
    ...(typeof search.vue === 'string' &&
    estCategorieDossier(search.vue) &&
    search.vue !== 'aujourdhui'
      ? { vue: search.vue }
      : {}),
    // L'enfant affiché suit le même principe que la catégorie : dans l'URL, pour qu'un
    // lien vers la fiche d'un enfant s'envoie et que « précédent » y revienne. Absent
    // signifie « tous ».
    ...(typeof search.enfant === 'string' && search.enfant ? { enfant: search.enfant } : {}),
  }),
  component: function EcranDossier() {
    const { dossierId } = useParams({ from: '/dossiers/$dossierId' });
    const { vue, enfant } = useSearch({ from: '/dossiers/$dossierId' });
    const naviguer = useNavigate();
    return (
      <Dossier
        dossierId={dossierId}
        categorie={vue ?? 'aujourdhui'}
        surCategorie={(categorie) =>
          void naviguer({
            to: '/dossiers/$dossierId',
            params: { dossierId },
            // « Aujourd'hui » retire le paramètre plutôt que de l'écrire : l'URL la plus
            // partagée reste la plus simple. Le choix d'enfant, lui, survit au changement
            // de catégorie — on ne le repose pas à chaque onglet.
            search: {
              ...(categorie === 'aujourdhui' ? {} : { vue: categorie }),
              ...(enfant ? { enfant } : {}),
            },
          })
        }
        enfantId={enfant ?? null}
        surEnfant={(enfantId) =>
          void naviguer({
            to: '/dossiers/$dossierId',
            params: { dossierId },
            search: {
              ...(vue ? { vue } : {}),
              ...(enfantId ? { enfant: enfantId } : {}),
            },
          })
        }
      />
    );
  },
});

/**
 * Retours de paiement Stripe (T-031).
 *
 * Ces deux adresses sont celles que l'API déclare à Stripe (`success_url`,
 * `cancel_url`) : elles doivent exister ici, sinon un parent qui vient de payer atterrit
 * sur « Not Found » — constaté en qualification le 2026-08-27.
 *
 * Elles exigent une session comme le reste : le retour de Stripe ne prouve pas une
 * identité, et l'activation, elle, vient du webhook signé.
 */
const merciAbonnement = createRoute({
  getParentRoute: () => racine,
  path: '/abonnement/merci',
  beforeLoad: exigeSession,
  // Stripe remplace `{CHECKOUT_SESSION_ID}` par l'identifiant réel à la redirection. Son
  // absence est une information à part entière : sans lui, l'écran ne sait rien d'un
  // paiement et ne doit surtout pas en affirmer un.
  validateSearch: (search: Record<string, unknown>): { session?: string } =>
    typeof search.session === 'string' && search.session ? { session: search.session } : {},
  component: function EcranMerci() {
    const naviguer = useNavigate();
    const { session } = useSearch({ from: '/abonnement/merci' });
    return (
      <MerciAbonnement
        {...(session !== undefined ? { sessionId: session } : {})}
        surRetour={() => void naviguer({ to: '/' })}
      />
    );
  },
});

const abonnementAbandonne = createRoute({
  getParentRoute: () => racine,
  path: '/abonnement',
  beforeLoad: exigeSession,
  component: function EcranAbandon() {
    const naviguer = useNavigate();
    return <AbonnementAbandonne surRetour={() => void naviguer({ to: '/' })} />;
  },
});

const rejoindre = createRoute({
  getParentRoute: () => racine,
  path: '/rejoindre',
  // Volontairement sans `exigeSession` : c'est la seule route du produit conçue pour
  // quelqu'un qui n'a pas encore de compte. L'y renvoyer vers la connexion lui ferait
  // créer un compte, ressaisir l'adresse qui vient de recevoir le lien, puis revenir —
  // quatre pas au moment précis où l'on abandonne. L'écran s'en charge lui-même.

  validateSearch: (search: Record<string, unknown>): { jeton: string } => ({
    jeton: typeof search.jeton === 'string' ? search.jeton : '',
  }),
  component: function EcranJonction() {
    const { jeton } = useSearch({ from: '/rejoindre' });
    const naviguer = useNavigate();
    return (
      <Rejoindre
        jeton={jeton}
        surArrivee={(id) => void naviguer({ to: '/dossiers/$dossierId', params: { dossierId: id } })}
      />
    );
  },
});


const motDePasseOublie = createRoute({
  getParentRoute: () => racine,
  path: '/mot-de-passe-oublie',
  component: function EcranMotDePasseOublie() {
    const naviguer = useNavigate();
    return <MotDePasseOublie surRetour={() => void naviguer({ to: '/connexion' })} />;
  },
});

const reinitialiserMotDePasse = createRoute({
  getParentRoute: () => racine,
  path: '/reinitialiser-mot-de-passe',
  // Le jeton arrive par l'URL, comme dans l'e-mail. Absent, l'écran le dit tout de suite
  // plutôt que de laisser remplir un formulaire qui échouera à l'envoi.
  validateSearch: (search: Record<string, unknown>): { jeton: string } => ({
    jeton: typeof search.jeton === 'string' ? search.jeton : '',
  }),
  component: function EcranReinitialisation() {
    const { jeton } = useSearch({ from: '/reinitialiser-mot-de-passe' });
    const naviguer = useNavigate();
    return (
      <ReinitialiserMotDePasse
        jeton={jeton}
        surConnexion={() => void naviguer({ to: '/connexion' })}
      />
    );
  },
});

// Cible du lien de confirmation d'adresse (`mail.service.ts`, qui compose
// `${APP_WEB_URL}/verifier-email?jeton=…`). Le chemin et le nom du paramètre sont dictés
// par l'e-mail déjà parti : les changer ici casserait les liens en circulation.
const verifierEmail = createRoute({
  getParentRoute: () => racine,
  path: '/verifier-email',
  validateSearch: (search: Record<string, unknown>): { jeton: string } => ({
    jeton: typeof search.jeton === 'string' ? search.jeton : '',
  }),
  component: function EcranVerificationEmail() {
    const { jeton } = useSearch({ from: '/verifier-email' });
    const naviguer = useNavigate();
    return <VerifierEmail jeton={jeton} surConnexion={() => void naviguer({ to: '/connexion' })} />;
  },
});

// Cible du lien de validation d'une suppression de dossier (`mail.service.ts`, qui compose
// `${APP_WEB_URL}/supprimer-dossier?jeton=…`). Le chemin et le nom du paramètre sont dictés
// par l'e-mail déjà parti : les changer ici casserait les liens en circulation.
//
// `exigeSession`, contrairement à `/rejoindre` : le parent à qui l'on demande de valider une
// destruction a forcément un compte — c'est le sien. Exiger le lien **et** la session évite
// qu'un message transféré suffise à détruire un dossier.
const supprimerDossier = createRoute({
  getParentRoute: () => racine,
  path: '/supprimer-dossier',
  beforeLoad: exigeSession,
  validateSearch: (search: Record<string, unknown>): { jeton: string } => ({
    jeton: typeof search.jeton === 'string' ? search.jeton : '',
  }),
  component: function EcranSuppressionDossier() {
    const { jeton } = useSearch({ from: '/supprimer-dossier' });
    const naviguer = useNavigate();
    return (
      <SupprimerDossier
        jeton={jeton}
        surDossier={(id) => void naviguer({ to: '/dossiers/$dossierId', params: { dossierId: id } })}
      />
    );
  },
});

/**
 * Le compte et ses réglages, derrière le menu de l'en-tête.
 *
 * Deux routes et non une page à onglets : « Mon compte » se consulte — où en est
 * l'abonnement, qui est en face dans chaque dossier — tandis qu'« Options » se règle. Les
 * réunir obligerait à choisir un onglet par défaut, donc à faire passer l'un des deux
 * derrière un clic de plus pour tout le monde.
 */
const monCompte = createRoute({
  getParentRoute: () => racine,
  path: '/compte',
  beforeLoad: exigeSession,
  component: function EcranMonCompte() {
    const naviguer = useNavigate();
    return (
      <MonCompte
        surDossier={(id) => void naviguer({ to: '/dossiers/$dossierId', params: { dossierId: id } })}
      />
    );
  },
});

const options = createRoute({
  getParentRoute: () => racine,
  path: '/options',
  beforeLoad: exigeSession,
  component: Options,
});

/**
 * Back-office d'exploitation.
 *
 * `/b-admin` plutôt que `/admin` : le second est la première adresse que teste n'importe
 * quel scanner. Ce n'est pas une protection — la garde et le rôle en sont une — mais il n'y
 * a aucune raison de mettre la porte là où l'on frappe.
 *
 * Chargé à la demande : le code du back-office et sa feuille de style ne descendent pas
 * dans le navigateur des parents, qui sont tout le monde sauf une personne.
 */
const backOffice = createRoute({
  getParentRoute: () => racine,
  path: '/b-admin',
  beforeLoad: exigeAdmin,
  component: lazyRouteComponent(() => import('../admin/Admin'), 'Admin'),
});

/**
 * Diagnostic des dépendances, à `/self.diagnose`.
 *
 * L'adresse est délibérément à part de `/b-admin` : on l'ouvre juste après un déploiement,
 * quand on ne sait pas encore ce qui est cassé, et un chemin qui ne traverse pas le
 * back-office est alors un chemin de moins à mettre en cause. Le point dans le nom la met
 * hors de portée de tout ce que le produit pourrait vouloir appeler « diagnostic » plus
 * tard.
 *
 * Même garde que le back-office, et pour une raison plus forte : le rapport nomme les
 * briques branchées, les adresses de retour déclarées chez les fournisseurs d'identité et
 * les services qui refusent nos identifiants. L'API la garde de son côté ; celle-ci évite
 * seulement d'afficher un écran que le serveur refusera de remplir.
 *
 * Chargé à la demande, comme le back-office : son code ne descend pas dans le navigateur
 * des parents.
 */
const pageDiagnostic = createRoute({
  getParentRoute: () => racine,
  path: '/self.diagnose',
  beforeLoad: exigeAdmin,
  component: lazyRouteComponent(() => import('../admin/PageDiagnostic'), 'PageDiagnostic'),
});

export const arbreDeRoutes = racine.addChildren([
  accueil,
  connexion,
  inscription,
  creerDossier,
  dossier,
  rejoindre,
  motDePasseOublie,
  reinitialiserMotDePasse,
  verifierEmail,
  supprimerDossier,
  monCompte,
  options,
  merciAbonnement,
  abonnementAbandonne,
  backOffice,
  pageDiagnostic,
]);

export function creerRouteur(session: Session) {
  return createRouter({
    routeTree: arbreDeRoutes,
    context: { session } satisfies ContexteRouteur,
    defaultPreload: false,
    // L'application est servie sous `/app` — la vitrine tient la racine. Sans
    // ce préfixe, le routeur compare `/app` à un arbre qui commence à `/`, ne
    // trouve rien, et rend sa page « Not Found » DANS une coquille par ailleurs
    // correcte : les assets chargent, l'en-tête s'affiche, seul le contenu
    // manque. Symptôme trompeur, constaté en qualification le 2026-08-26.
    //
    // Même source que `base` dans vite.config.ts, et il faut les deux : `base`
    // corrige les URL des ASSETS, `basepath` corrige celles des ROUTES.
    // `import.meta.env.BASE_URL` vaut `/app/` après compilation et `/` sous le
    // serveur de développement — les deux restent donc justes sans condition.
    // Barre finale retiree : BASE_URL vaut '/app/' apres compilation et '/' en
    // developpement. On ne parie pas sur la normalisation interne du routeur.
    basepath: import.meta.env.BASE_URL.replace(/\/$/, '') || '/',
  });
}

/**
 * Déclare le routeur auprès de TanStack Router.
 *
 * Sans cet enregistrement, `useParams` et `useSearch` renvoient `any` : les identifiants
 * d'URL traverseraient l'application sans type, et une route renommée ne casserait rien
 * à la compilation. C'est précisément ce que le typage du routeur doit empêcher.
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof creerRouteur>;
  }
}
